import Image, { ImageProps } from "next/image";
import type {
  CSSProperties,
  HTMLAttributes,
  ImgHTMLAttributes,
  ReactNode,
} from "react";
import clsx from "clsx";

import { Callout } from "@/components/blog/Callout";
import { CTA } from "@/components/blog/CTA";
import { CTAGroup } from "@/components/blog/CTAGroup";
import { ROICalculator } from "@/components/blog/ROICalculator";
import SourceRef from "@/components/blog/SourceRef";
import ImageLightbox from "@/components/common/ImageLightbox";
import { GailBlue } from "@/components/common/GailBlue";
import type { MDXComponents } from "mdx/types";

function childrenToText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) {
    return children.map((c) => (typeof c === "string" ? c : "")).join("");
  }
  return "";
}

function slugify(children: ReactNode): string {
  return childrenToText(children)
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/(^-|-$)/g, "");
}

export function BlogImage({
  src,
  alt,
  width = 1600,
  height = 1600,
  className = "w-full h-auto rounded-lg shadow-md",
  style,
}: Readonly<{
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  style?: CSSProperties;
}>) {
  return (
    <ImageLightbox
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={className}
      style={style}
    />
  );
}

export function TwoColumn({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-col md:flex-row md:items-start gap-8 my-4 not-prose">
      {children}
    </div>
  );
}

export function TwoColumnText({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="md:w-2/5 prose prose-headings:mt-0 flex flex-col gap-8 shrink-0">
      {children}
    </div>
  );
}

export function TwoColumnMedia({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  return <div className="w-full md:w-3/5">{children}</div>;
}

export function useMDXComponents(
  components: MDXComponents = {},
): MDXComponents {
  return {
    img: (props) => {
      const { alt, ...rest } = props as ImgHTMLAttributes<HTMLImageElement>;
      return (
        <Image
          sizes="100vw"
          style={{ width: "100%", height: "auto" }}
          {...(rest as ImageProps)}
          alt={alt ?? ""}
        />
      );
    },
    a: ({ href, children, ...props }) => (
      <a
        href={href}
        className="text-gail-secondary underline hover:text-black"
        {...props}
      >
        {children}
      </a>
    ),
    p: ({ children, className, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
      <p
        className={clsx("my-4 text-black leading-relaxed", className)}
        {...props}
      >
        {children}
      </p>
    ),
    strong: ({
      children,
      className,
      ...props
    }: HTMLAttributes<HTMLElement>) => (
      <strong
        className={clsx("font-semibold text-black", className)}
        {...props}
      >
        {children}
      </strong>
    ),
    h2: ({ children }) => (
      <h2
        id={slugify(children)}
        className="mt-10 mb-4 text-2xl font-bold text-black scroll-mt-20"
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        id={slugify(children)}
        className="mt-8 mb-3 text-lg font-bold text-black scroll-mt-20"
      >
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4
        id={slugify(children)}
        className="mt-6 mb-2 text-base font-semibold text-black scroll-mt-20"
      >
        {children}
      </h4>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-6 space-y-2">{children}</ol>
    ),
    ul: ({ children }) => (
      <ul className="list-disc pl-6 space-y-2">{children}</ul>
    ),
    BlogImage,
    Callout,
    CTA,
    CTAGroup,
    GailBlue,
    ROICalculator,
    SourceRef,
    TwoColumn,
    TwoColumnText,
    TwoColumnMedia,
    ...components,
  };
}
